import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  useTheme,
} from '@mui/material';
import {
  AccountBalance as BalanceIcon,
  AccountBalanceWallet as WalletIcon,
  Info as InfoIcon,
  MonetizationOn as FeeIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

const formatBalance = (value, currency = 'USDT', showCurrency = true) => {
  if (value === null || value === undefined) return '—';

  const number = parseFloat(value);
  if (isNaN(number)) return '—';

  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    style: 'decimal',
    useGrouping: true
  }).format(number) + (showCurrency ? ` ${currency}` : '');
};

const FounderAccountCard = ({ user }) => {
  const theme = useTheme();

  if (!user) {
    return null;
  }

  const marginBalance = parseFloat(user.margin_balance || 0);
  const walletBalance = parseFloat(user.wallet_balance || 0);
  const totalPnl = parseFloat(user.total_pnl || 0);
  const monthlyPnl = parseFloat(user.monthly_pnl || 0); // Lợi nhuận theo tháng
  const dailyPnl = parseFloat(user.daily_pnl || 0); // Lợi nhuận hôm nay
  const dailyCommission = parseFloat(user.daily_commission || 0); // Phí commission hôm nay
  const dailyFundingPaid = parseFloat(user.daily_funding_paid || 0); // Phí funding hôm nay
  const dailyTotalFees = parseFloat(user.daily_total_fees || 0); // Tổng phí hôm nay
  const availableBalance = walletBalance - Math.abs(totalPnl) - Math.abs(marginBalance);
  const totalAsset = walletBalance - Math.abs(totalPnl);

  console.log('[FounderAccountCard] Debug:', {
    username: user.username,
    monthly_pnl_raw: user.monthly_pnl,
    monthlyPnl,
    daily_pnl_raw: user.daily_pnl,
    dailyPnl,
    margin_balance: user.margin_balance,
    marginBalance
  });

  const getStatusColor = (value) => {
    if (value === null || value === undefined) return 'text.secondary';
    const num = parseFloat(value);
    if (isNaN(num)) return 'text.secondary';
    return num >= 0 ? 'success.main' : 'error.main';
  };

  return (
    <Card
      elevation={3}
      sx={{
        borderRadius: 3,
        background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[100]} 100%)`,
        border: `1px solid ${theme.palette.divider}`,
        overflow: 'visible',
        position: 'relative',
        '&:hover': {
          boxShadow: theme.shadows[8]
        },
        transition: 'all 0.3s ease-in-out'
      }}
    >
      <CardContent>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 3
        }}>
          <Box>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ mb: 0.5 }}
            >
              {user.fullname || user.username}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                variant="h4"
                sx={{
                    fontWeight: 700,
                  color: getStatusColor(monthlyPnl),
                  lineHeight: 1.2,
                  mr: 1
                }}
              >
                {formatBalance(monthlyPnl, user.currency)}
              </Typography>
            </Box>
            {user.updated_at && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                <InfoIcon fontSize="inherit" sx={{ mr: 0.5 }} />
                Cập nhật lúc: {format(new Date(user.updated_at), 'HH:mm:ss')}
              </Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          p: 2,
          backgroundColor: theme.palette.mode === 'dark' ? 'background.default' : 'grey.50',
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`
        }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <WalletIcon sx={{ color: 'primary.main', mr: 1 }} />
              <Typography variant="subtitle2" color="text.primary">Tổng tài sản</Typography>
            </Box>
            <Typography variant="h6" fontWeight={700}>
              {formatBalance(walletBalance, user.currency, true)}
            </Typography>
          </Box>

          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            p: 2,
            backgroundColor: theme.palette.mode === 'dark' ? 'background.paper' : 'white',
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BalanceIcon sx={{
                  color: getStatusColor(marginBalance),
                  mr: 1,
                  fontSize: '1.1rem'
                }} />
                <Typography variant="body2" color="text.secondary">Số dư còn lại</Typography>
              </Box>
              <Typography
                variant="subtitle1"
                fontWeight={600}
              >
                {formatBalance(availableBalance, user.currency, true)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BalanceIcon sx={{
                  color: getStatusColor(marginBalance),
                  mr: 1,
                  fontSize: '1.1rem',
                  opacity: 0.7
                }} />
                <Typography variant="body2" color="text.secondary">Đang sử dụng</Typography>
              </Box>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                color={getStatusColor(marginBalance)}
              >
                {formatBalance(Math.abs(marginBalance), user.currency, true)}
              </Typography>
            </Box>

            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 2,
              pt: 2,
              borderTop: `1px solid ${theme.palette.divider}`
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Lãi/Lỗ hôm nay</Typography>
              </Box>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                color={dailyPnl >= 0 ? 'success.main' : 'error.main'}
              >
                {dailyPnl >= 0 ? '+' : ''}{dailyPnl.toFixed(2)} {user.currency || 'USDT'}
              </Typography>
            </Box>

            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 1,
              pt: 1,
              borderTop: `1px solid ${theme.palette.divider}`
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Tổng Lãi/Lỗ</Typography>
              </Box>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                color={totalPnl >= 0 ? 'success.main' : 'error.main'}
              >
                {totalPnl >= 0 ? '+' : ''}{totalPnl.toFixed(2)} {user.currency || 'USDT'}
              </Typography>
            </Box>

            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 2,
              pt: 2,
              borderTop: `1px solid ${theme.palette.divider}`
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FeeIcon sx={{ color: 'warning.main', mr: 1, fontSize: '1.1rem' }} />
                <Typography variant="body2" color="text.secondary">Phí hôm nay</Typography>
              </Box>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                color="warning.main"
              >
                -{dailyTotalFees.toFixed(2)} {user.currency || 'USDT'}
              </Typography>
            </Box>

            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              ml: 4,
              mt: 1
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">Commission:</Typography>
                <Typography variant="caption" color="text.secondary">
                  -{dailyCommission.toFixed(2)} {user.currency || 'USDT'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">Funding:</Typography>
                <Typography variant="caption" color="text.secondary">
                  -{dailyFundingPaid.toFixed(2)} {user.currency || 'USDT'}
                </Typography>
              </Box>
            </Box>

            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 2,
              pt: 1,
              borderTop: `1px solid ${theme.palette.divider}`
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <WalletIcon sx={{ color: 'primary.main', mr: 1, fontSize: '1.1rem' }} />
                <Typography variant="body2" color="primary" fontWeight={500}>Tổng tài sản còn lại</Typography>
              </Box>
              <Typography variant="subtitle1" fontWeight={700} color="primary">
                {formatBalance(totalAsset, user.currency, true)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default FounderAccountCard;
